import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useProducts } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency } from "@/lib/utils";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => {
      const name = row.original.name as string | undefined;
      return (
        <span className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: ({ row }) => {
      const category = row.original.category as string | undefined;
      return (
        <span className={category ? "" : "text-[color:var(--color-text-lo)]"}>
          {category || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "product_code",
    header: "Code",
    enableSorting: false,
    cell: ({ row }) => {
      const code = row.original.product_code as string | undefined;
      return (
        <span
          className={code ? "typo-mono" : "text-[color:var(--color-text-lo)]"}
        >
          {code || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "unit_price",
    header: "Price",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.unit_price != null
          ? formatCurrency(row.original.unit_price as number)
          : "—"}
      </span>
    ),
  },
  {
    accessorKey: "unit_type",
    header: "Unit",
    enableSorting: false,
    cell: ({ row }) => {
      const unit = row.original.unit_type as string | undefined;
      return (
        <span className={unit ? "" : "text-[color:var(--color-text-lo)]"}>
          {unit || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "is_active",
    header: "Status",
    cell: ({ row }) => (
      <StatusBadge status={row.original.is_active ? "active" : "inactive"} />
    ),
  },
];

export default function V2ProductsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useProducts({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const products = (data?.products ?? []) as Row[];
  const total = data?.total ?? 0;

  return (
    <ListPage
      title="Products"
      subtitle={`${total.toLocaleString()} total products`}
    >
      <DataTable
        columns={columns}
        data={products}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/products/${row.id}`)}
        emptyMessage="No products yet"
        emptyDescription="Add a service or product to start building proposals."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
